import dayjs from "dayjs";
import { PRESERVE_CASE_CONSTANTS } from "shared";

export const emptyStringHandler = (data?: string | null) => {
  return data && data !== null ? data : "";
};

export const collapseMultipleSpaces = (data?: string) => {
  return data?.replace(/\s+/g, " ").trim() || "";
};

export const getFullName = (
  firstName?: string | null,
  middleName?: string | null,
  lastName?: string | null,
) => {
  const firstNameNew = emptyStringHandler(firstName);
  const middleNameNew = emptyStringHandler(middleName);
  const lastNameNew = emptyStringHandler(lastName);
  const fullName = `${firstNameNew} ${middleNameNew} ${lastNameNew}`;
  return collapseMultipleSpaces(fullName) || "";
};

/**
 * Prefixes a count and pluralizes the noun based on it.
 * pluralize({ count: 1, singular: "ticket" }) → "1 ticket".
 * pluralize({ count: 3, singular: "ticket" }) → "3 tickets".
 * Pass an explicit plural for irregular words:
 * pluralize({ count: 2, singular: "entity", plural: "entities" }).
 */
export const pluralize = ({
  count,
  singular,
  plural = `${singular}s`,
}: {
  count: number;
  singular: string;
  plural?: string;
}): string => `${count} ${count === 1 ? singular : plural}`;

export const stringToNumber = (data?: string) => {
  try {
    return data && data !== null ? parseInt(data) : 0;
  } catch {
    return 0;
  }
};

// DOMParser builds an inert document — unlike innerHTML on a detached element,
// it never runs inline handlers or loads images (e.g. <img onerror=…>), so
// user-entered HTML is safe to pass here.
export const extractStringFromHtml = (value: string) =>
  new DOMParser().parseFromString(value, "text/html").body.textContent ?? "";

export const capitalizeString = (inputString: string) => {
  return inputString?.[0]?.toUpperCase() + inputString?.slice(1);
};

/**
 * An ID-like word — capitals and digits together, e.g. "INV12528",
 * "STALL10142600001" or "(INV12528)." — which must keep its exact casing.
 * Surrounding punctuation is ignored when deciding.
 */
const isIdLikeWord = (word: string) => {
  const core = word.replace(/^[^A-Za-z0-9]+|[^A-Za-z0-9]+$/g, "");
  return /[A-Z]/.test(core) && /\d/.test(core);
};

export const convertStringToSentenceCase = (inputString: string) => {
  if (!inputString) return "";
  if (typeof inputString !== "string") return inputString;
  const words = inputString?.split(" ");
  let isStartOfSentence = true;
  return words
    .map((word) => {
      // First, check if it's an acronym (2-4 uppercase letters) or an ID
      // ("INV12528") - preserve as-is
      if (
        /^[A-Z]{2,4}$/.test(word) ||
        isIdLikeWord(word) ||
        PRESERVE_CASE_CONSTANTS?.includes(word)
      ) {
        isStartOfSentence = /[.!?;]$/.test(word);
        return word;
      }
      let processedWord: string;
      if (isStartOfSentence) {
        processedWord =
          word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase();
      } else {
        processedWord = word?.toLowerCase();
      }
      isStartOfSentence = /[.!?;]$/.test(word);
      return processedWord;
    })
    .join(" ");
};

export const generateUniqueFileName = (
  existingNames: string[],
  baseName: string,
): string => {
  // Normalize the base name by trimming whitespace
  let normalizedBaseName = baseName.trim();
  const index = normalizedBaseName.lastIndexOf("copy");
  // remove substrings after copy
  if (index !== -1) {
    normalizedBaseName = normalizedBaseName.substring(0, index).trim();
  }

  // Initialize the new file name as the base name
  let newFileName = normalizedBaseName;
  let counter = 0;

  // Check if the new file name already exists in the existing names
  while (existingNames.includes(newFileName)) {
    // Generate a new file name with a counter
    newFileName = `${normalizedBaseName} copy${
      counter === 0 ? "" : ` (${counter})`
    }`;
    counter++;
  }

  return newFileName;
};

export const minifyHtml = (html: string, removeScripts = true): string => {
  if (!html) return "";

  try {
    // Check if we're in a browser environment
    const isBrowser =
      typeof window !== "undefined" && typeof document !== "undefined";

    // List of dangerous tags that should be removed (reduced list)
    const dangerousTags = new Set([
      "script",
      "iframe",
      "object",
      "embed",
      "applet",
      "frame",
      "frameset",
      "bgsound",
      "base",
      "noscript",
      "template",
    ]);

    // List of dangerous attributes that should be removed
    const dangerousAttributes = new Set([
      "onerror",
      "onload",
      "onclick",
      "onmouseover",
      "onmouseout",
      "onkeydown",
      "onkeyup",
      "onkeypress",
      "onfocus",
      "onblur",
      "onchange",
      "onsubmit",
      "onreset",
      "onselect",
      "onunload",
      "onabort",
      "oncanplay",
      "oncanplaythrough",
      "ondurationchange",
      "onemptied",
      "onended",
      "oninput",
      "oninvalid",
      "onpause",
      "onplay",
      "onplaying",
      "onprogress",
      "onratechange",
      "onreadystatechange",
      "onseeked",
      "onseeking",
      "onstalled",
      "onsuspend",
      "ontimeupdate",
      "onvolumechange",
      "onwaiting",
      "onwheel",
      "oncopy",
      "oncut",
      "onpaste",
      "onbeforecopy",
      "onbeforecut",
      "onbeforepaste",
      "oncontextmenu",
      "ondrag",
      "ondragend",
      "ondragenter",
      "ondragleave",
      "ondragover",
      "ondragstart",
      "ondrop",
      "onmousedown",
      "onmousemove",
      "onmouseup",
      "onmousewheel",
      "onscroll",
      "onresize",
      "onstorage",
      "ontoggle",
      "onhashchange",
      "onmessage",
      "onoffline",
      "ononline",
      "onpagehide",
      "onpageshow",
      "onpopstate",
      "onredo",
      "onundo",
      "onbeforeunload",
      "onbeforeprint",
      "onafterprint",
      "ondblclick",
    ]);

    // List of safe URL protocols
    const safeUrlProtocols = new Set([
      "http:",
      "https:",
      "mailto:",
      "tel:",
      "ftp:",
      "sftp:",
      "ssh:",
      "irc:",
      "ircs:",
      "magnet:",
      "news:",
      "nntp:",
      "sip:",
      "sips:",
      "sms:",
      "smsto:",
      "telnet:",
      "urn:",
      "webcal:",
      "xmpp:",
    ]);

    // Function to sanitize URLs
    const sanitizeUrl = (url: string): string => {
      try {
        // Allow relative URLs
        if (
          url.startsWith("/") ||
          url.startsWith("./") ||
          url.startsWith("../")
        ) {
          return url;
        }
        const urlObj = new URL(url);
        return safeUrlProtocols.has(urlObj.protocol.toLowerCase()) ? url : "";
      } catch {
        // If URL parsing fails, check if it's a relative URL or fragment
        if (
          url.startsWith("#") ||
          url.startsWith("/") ||
          url.startsWith("./") ||
          url.startsWith("../")
        ) {
          return url;
        }
        return "";
      }
    };

    // Function to sanitize CSS (less aggressive)
    const sanitizeCss = (css: string): string => {
      // Only remove the most dangerous CSS functions
      return css
        .replace(/expression\s*\(/gi, "")
        .replace(/javascript:/gi, "")
        .replace(/vbscript:/gi, "")
        .replace(/data:\s*(?!image\/)/gi, "") // Allow data:image/ but not other data: URLs
        .replace(/@import\s*[^;]*(;|$)/gi, "");
    };

    if (isBrowser) {
      // Browser-based implementation
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;

      // Remove dangerous elements
      const allElements = tempDiv.getElementsByTagName("*");
      for (let i = allElements.length - 1; i >= 0; i--) {
        const element = allElements[i];
        const tagName = element.tagName.toLowerCase();

        // Remove dangerous tags
        if (dangerousTags.has(tagName)) {
          element.remove();
          continue;
        }

        // Sanitize attributes
        Array.from(element.attributes).forEach((attr) => {
          const attrName = attr.name.toLowerCase();
          const attrValue = attr.value;

          // Remove dangerous attributes (event handlers)
          if (dangerousAttributes.has(attrName)) {
            element.removeAttribute(attrName);
            return;
          }

          // Sanitize URLs in href and src attributes
          if (attrName === "href" || attrName === "src") {
            const sanitizedUrl = sanitizeUrl(attrValue);
            if (sanitizedUrl !== attrValue) {
              if (sanitizedUrl) {
                element.setAttribute(attrName, sanitizedUrl);
              } else {
                element.removeAttribute(attrName);
              }
            }
            return;
          }

          // Sanitize style attribute (less aggressive)
          if (attrName === "style") {
            const sanitizedCss = sanitizeCss(attrValue);
            if (sanitizedCss !== attrValue) {
              element.setAttribute(attrName, sanitizedCss);
            }
            return;
          }

          // Keep safe data attributes (don't remove all data attributes)
          if (attrName.startsWith("data-")) {
            // Only remove data attributes that could be dangerous
            if (
              attrName.includes("on") ||
              attrName.includes("script") ||
              attrName.includes("eval")
            ) {
              element.removeAttribute(attrName);
            }
            return;
          }
        });
      }

      // Return the sanitized HTML
      return tempDiv.innerHTML;
    } else {
      // Node.js environment implementation (less aggressive)
      let processedHtml = html;

      // Remove dangerous tags and their content
      dangerousTags.forEach((tag) => {
        const regex = new RegExp(
          `<${tag}\\b[^<]*(?:(?!<\\/${tag}>)<[^<]*)*<\\/${tag}>`,
          "gi",
        );
        processedHtml = processedHtml.replace(regex, "");
      });

      // Remove dangerous attributes (event handlers only)
      dangerousAttributes.forEach((attr) => {
        const regex = new RegExp(`\\s${attr}=["'][^"']*["']`, "gi");
        processedHtml = processedHtml.replace(regex, "");
      });

      // Sanitize URLs in href and src attributes
      processedHtml = processedHtml.replace(
        /(href|src)=["']([^"']*)["']/gi,
        (match, attr, url) => {
          const sanitizedUrl = sanitizeUrl(url);
          return sanitizedUrl ? `${attr}="${sanitizedUrl}"` : "";
        },
      );

      // Sanitize style attributes (less aggressive)
      processedHtml = processedHtml.replace(
        /style=["']([^"']*)["']/gi,
        (match, css) => {
          const sanitizedCss = sanitizeCss(css);
          return `style="${sanitizedCss}"`;
        },
      );

      return processedHtml;
    }
  } catch (error) {
    console.error("Error minifying HTML:", error);
    return html; // Return original HTML if there's an error
  }
};

export const beautifyHtml = (html: string, indentSize = 2): string => {
  if (!html) return "";

  try {
    // Check if we're in a browser environment
    const isBrowser =
      typeof window !== "undefined" && typeof document !== "undefined";

    if (!isBrowser) {
      // Fallback for Node.js environment - use simpler string-based approach
      return beautifyHtmlSimple(html, indentSize);
    }

    // Create a DOM parser to properly parse HTML
    const parser = new DOMParser();

    // Wrap the HTML in a temporary container to ensure proper parsing
    const wrappedHtml = `<div class="temp-wrapper">${html}</div>`;
    const doc = parser.parseFromString(wrappedHtml, "text/html");

    // Get the content from the temporary wrapper
    const tempWrapper = doc.querySelector(".temp-wrapper");
    if (!tempWrapper) {
      return beautifyHtmlSimple(html, indentSize);
    }

    // List of known self-closing tags
    const selfClosingTags = new Set([
      "area",
      "base",
      "br",
      "col",
      "embed",
      "hr",
      "img",
      "input",
      "link",
      "meta",
      "param",
      "source",
      "track",
      "wbr",
    ]);

    // List of inline elements that should be handled carefully
    const inlineElements = new Set([
      "a",
      "abbr",
      "acronym",
      "b",
      "bdo",
      "big",
      "br",
      "button",
      "cite",
      "code",
      "dfn",
      "em",
      "i",
      "img",
      "input",
      "kbd",
      "label",
      "map",
      "object",
      "output",
      "q",
      "samp",
      "script",
      "select",
      "small",
      "span",
      "strong",
      "sub",
      "sup",
      "textarea",
      "time",
      "tt",
      "var",
    ]);

    // Function to check if element has only inline children
    const hasOnlyInlineChildren = (element: Element): boolean => {
      for (const child of element.childNodes) {
        if (child.nodeType === Node.ELEMENT_NODE) {
          const childElement = child as Element;
          if (!inlineElements.has(childElement.tagName.toLowerCase())) {
            return false;
          }
        }
      }
      return true;
    };

    // Function to recursively format the HTML nodes
    const formatNode = (node: Node, level = 0): string => {
      const indent = " ".repeat(indentSize * level);

      // Text node
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || "";
        const trimmedText = text.trim();

        // Preserve whitespace for non-empty text nodes
        if (trimmedText) {
          // For text with only whitespace, preserve a single space
          if (text !== trimmedText && /^\s+$/.test(text)) {
            return " ";
          }
          return trimmedText;
        }
        return "";
      }

      // Element node
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        const tagName = element.tagName.toLowerCase();
        const attributes = Array.from(element.attributes)
          .map((attr: Attr) => `${attr.name}="${attr.value}"`)
          .join(" ");

        // Handle self-closing tags
        if (selfClosingTags.has(tagName)) {
          return `${indent}<${tagName}${attributes ? ` ${attributes}` : ""} />`;
        }

        // For empty elements
        if (!element.childNodes.length) {
          return `${indent}<${tagName}${
            attributes ? ` ${attributes}` : ""
          }></${tagName}>`;
        }

        // For inline elements with simple text content
        if (
          inlineElements.has(tagName) &&
          element.childNodes.length === 1 &&
          element.childNodes[0].nodeType === Node.TEXT_NODE
        ) {
          const content = element.textContent?.trim() || "";
          return `<${tagName}${
            attributes ? ` ${attributes}` : ""
          }>${content}</${tagName}>`;
        }

        // For elements that contain only inline children (keep on one line)
        if (hasOnlyInlineChildren(element)) {
          let content = "";
          Array.from(element.childNodes).forEach((child) => {
            content += formatNode(child, 0);
          });
          return `${indent}<${tagName}${
            attributes ? ` ${attributes}` : ""
          }>${content}</${tagName}>`;
        }

        // For block elements with multiple children
        let result = `${indent}<${tagName}${
          attributes ? ` ${attributes}` : ""
        }>`;
        let hasBlockChildren = false;

        Array.from(element.childNodes).forEach((child) => {
          if (child.nodeType === Node.ELEMENT_NODE) {
            const childElement = child as Element;
            if (!inlineElements.has(childElement.tagName.toLowerCase())) {
              hasBlockChildren = true;
            }
          }
        });

        if (hasBlockChildren) {
          result += "\n";
          Array.from(element.childNodes).forEach((child) => {
            const formattedChild = formatNode(child, level + 1);
            if (formattedChild.trim()) {
              if (child.nodeType === Node.ELEMENT_NODE) {
                result += `${formattedChild}\n`;
              } else {
                result += `${" ".repeat(indentSize * (level + 1)) + formattedChild}\n`;
              }
            }
          });
          result += indent;
        } else {
          Array.from(element.childNodes).forEach((child) => {
            result += formatNode(child, level + 1);
          });
        }

        result += `</${tagName}>`;
        return result;
      }

      return "";
    };

    // Start formatting from the children of the temporary wrapper
    let result = "";
    Array.from(tempWrapper.childNodes).forEach((node) => {
      const formatted = formatNode(node);
      if (formatted.trim()) {
        result += `${formatted}\n`;
      }
    });

    return result.trim();
  } catch (error) {
    console.error("Error beautifying HTML:", error);
    return beautifyHtmlSimple(html, indentSize);
  }
};

// Simple string-based beautifier as fallback
const beautifyHtmlSimple = (html: string, indentSize = 2): string => {
  let formatted = "";
  let indent = 0;
  const indentStr = " ".repeat(indentSize);

  // List of known self-closing tags
  const selfClosingTags = new Set([
    "area",
    "base",
    "br",
    "col",
    "embed",
    "hr",
    "img",
    "input",
    "link",
    "meta",
    "param",
    "source",
    "track",
    "wbr",
  ]);

  // Split the HTML string into tags and text
  const parts = html.split(/(<\/?[^>]+>)/g);

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (!part.trim()) continue;

    // Check if it's a closing tag
    if (part.startsWith("</")) {
      indent = Math.max(0, indent - 1);
      formatted += `${indentStr.repeat(indent) + part}\n`;
    }
    // Check if it's an opening tag
    else if (part.startsWith("<") && !part.startsWith("</")) {
      formatted += `${indentStr.repeat(indent) + part}\n`;

      // Check if it's not a self-closing tag
      if (
        !part.endsWith("/>") &&
        !selfClosingTags.has(part.match(/<([^\s>]+)/)?.[1]?.toLowerCase() || "")
      ) {
        indent++;
      }
    }
    // It's text content
    else {
      const trimmedPart = part.trim();
      if (trimmedPart) {
        formatted += `${indentStr.repeat(indent) + trimmedPart}\n`;
      }
    }
  }

  return formatted.trim();
};

/**
 * Removes unnecessary spaces between HTML tags when there's no meaningful content
 * @param html - The HTML string to process
 * @param preservePreformatted - Whether to preserve spaces in <pre>, <code>, and <textarea> tags
 * @returns The HTML string with unnecessary spaces removed
 */
export const removeSpacesBetweenTags = (
  html: string,
  preservePreformatted = true,
): string => {
  if (!html) return "";

  try {
    // Check if we're in a browser environment
    const isBrowser =
      typeof window !== "undefined" && typeof document !== "undefined";

    // Tags where whitespace should be preserved
    const preformattedTags = new Set([
      "pre",
      "code",
      "textarea",
      "script",
      "style",
    ]);

    if (isBrowser) {
      // Browser-based implementation using DOM
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;

      // Function to check if we're inside a preformatted tag
      const isInsidePreformatted = (node: Node): boolean => {
        let current = node.parentNode;
        while (current && current !== tempDiv) {
          if (
            current.nodeType === Node.ELEMENT_NODE &&
            preformattedTags.has((current as Element).tagName.toLowerCase())
          ) {
            return true;
          }
          current = current.parentNode;
        }
        return false;
      };

      // Function to clean whitespace text nodes
      const cleanWhitespace = (node: Node): void => {
        if (node.nodeType === Node.TEXT_NODE) {
          // Skip if inside preformatted tags and preservePreformatted is true
          if (preservePreformatted && isInsidePreformatted(node)) {
            return;
          }

          const text = node.textContent || "";

          // If the text node contains only whitespace
          if (/^\s+$/.test(text)) {
            const prevSibling = node.previousSibling;
            const nextSibling = node.nextSibling;

            // Check if both adjacent siblings are element nodes (tags)
            if (
              prevSibling &&
              nextSibling &&
              prevSibling.nodeType === Node.ELEMENT_NODE &&
              nextSibling.nodeType === Node.ELEMENT_NODE
            ) {
              // Remove whitespace between any tags (block or inline)
              node.textContent = "";
            } else if (
              (prevSibling && prevSibling.nodeType === Node.ELEMENT_NODE) ||
              (nextSibling && nextSibling.nodeType === Node.ELEMENT_NODE)
            ) {
              // Remove whitespace at the edge of containers
              node.textContent = "";
            }
          }
          // Do not normalize whitespace within text content - preserve original spacing
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          // Recursively process child nodes
          const children = Array.from(node.childNodes);
          children.forEach(cleanWhitespace);
        }
      };

      // Clean the whitespace
      cleanWhitespace(tempDiv);

      return tempDiv.innerHTML;
    } else {
      // String-based implementation for Node.js environment
      let processedHtml = html;

      // Remove whitespace between closing and opening tags
      processedHtml = processedHtml.replace(/>\s+</g, "><");

      // Remove leading and trailing whitespace within tags
      processedHtml = processedHtml.replace(/(<[^>]+>)\s+/g, "$1");
      processedHtml = processedHtml.replace(/\s+(<\/[^>]+>)/g, "$1");

      // If preservePreformatted is true, restore spaces in preformatted tags
      if (preservePreformatted) {
        const preformattedPattern = `(${Array.from(preformattedTags).join(
          "|",
        )})`;
        const preformattedRegex = new RegExp(
          `(<${preformattedPattern}[^>]*>)(.*?)(</${preformattedPattern}>)`,
          "gis",
        );

        // Store original preformatted content
        const originalHtml = html;
        processedHtml = processedHtml.replace(
          preformattedRegex,
          (match, openTag, content, closeTag) => {
            // Find the original content for this preformatted block
            const originalMatch = originalHtml.match(
              new RegExp(
                openTag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") +
                  "(.*?)" +
                  closeTag.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
                "is",
              ),
            );
            return originalMatch ? originalMatch[0] : match;
          },
        );
      }

      return processedHtml;
    }
  } catch (error) {
    console.error("Error removing spaces between tags:", error);
    return html; // Return original HTML if there's an error
  }
};

export const addDateTimeToFileName = (fileName: string) => {
  const formattedDate = dayjs().format("YYYYMMDDHHmmss");
  return `${fileName}_${formattedDate}`;
};

export const joinArrayWithSymbol = (array: string[], symbol: string = ", ") => {
  return array.length > 0
    ? array
        .map((value) => {
          try {
            return value ? value.trim() : value;
          } catch {
            return value;
          }
        })
        .filter((item) => item && item !== "")
        .join(symbol)
    : "";
};

export const duplicateName = (
  name: string,
  nameList: string[],
  count?: number,
): string => {
  const counter = count || 1;
  const newName = `${name} (${counter})`;
  if (nameList.includes(newName)) {
    if (counter <= 10000) {
      return duplicateName(name, nameList, counter + 1);
    } else {
      return `${name} (${Date.now()})`;
    }
  }
  return newName;
};
